import { z } from 'zod/v3';

/**
 * RFC-239 canonical schema: CODE_CHANGE.
 *
 * A change merged into a code repository — today a GitHub pull request.
 * This is the "changes" asset population auditors sample (RFC-239 §3.1):
 * every PR merged into the default branch inside the audit window, with the
 * attributes an auditor tests against (author, approvers, review count,
 * merge time). Deliberately narrow; deployment evidence (#1689) is a
 * different question and stays on its own collector.
 */

export const CODE_CHANGE_SCHEMA_VERSION = 2;

export const CodeChange = z.object({
  /** Stable provider identifier: `github:<owner/repo>#<number>`. */
  externalId: z.string(),
  /** Human identifier the auditor sees: `#123`. */
  identifier: z.string(),
  /** Parent repository, `owner/repo`. */
  repository: z.string(),
  title: z.string(),
  url: z.string(),
  /** Author login. Null when the provider omitted it. */
  author: z.string().nullable(),
  /** Lifecycle. A population of merged changes only contains `merged`. */
  state: z.enum(['open', 'closed', 'merged']),
  draft: z.boolean(),
  baseBranch: z.string(),
  headSha: z.string().nullable(),
  /** Non-author logins with pre-merge approval; null when not collected or timing is unknown. */
  approvers: z.array(z.string()).nullable(),
  /** Count of all reviews (any state) on the change. */
  reviewCount: z.number().int().nullable(),
  /** Roll-up of check runs on the merged head commit: `success` when every
   *  completed run succeeded, `failure` when any failed, `pending` when some
   *  had not completed, `none` when the commit had no check runs, `unknown`
   *  when the collector could not read them (missing Actions scope). */
  checksConclusion: z.enum(['success', 'failure', 'pending', 'none', 'unknown']),
  checkRunCount: z.number().int().nullable(),
  createdAt: z.string().datetime().nullable(),
  mergedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  providerExtensions: z.record(z.unknown()).optional(),
});

export type CodeChange = z.infer<typeof CodeChange>;
